import React, { forwardRef, useImperativeHandle, useState } from "react";
import { Alert, Box, Switch, Typography, Tooltip, useTheme } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import InfoIcon from "@mui/icons-material/Info";
import { getQuery, setQuery } from "../../../../../services/local-session-storage";
import Periods from "../../../../../utils/periods";
import { ChoiceChips } from "../../../../../components/ui";
import { getPeriodLabel } from "../../../../../utils/period-label";
import ToggleRow from "./components/ToggleRow";
import DefinitionsDialogs from "./components/DefinitionsDialogs";

// ----------------------------------------------------------------------

type DefinitionsModel = {
    vegan: boolean;
    period: string;
    organic: boolean;
    pregnant: boolean;
    activeList: boolean;
};

type Props = {
    onFinish: () => void;
    onBack: () => void;
};

export interface DefinitionsTopicalHandle {
    submit: () => void;
}

// ----------------------------------------------------------------------

export const DefinitionsTopical = forwardRef<DefinitionsTopicalHandle, Props>(({ onFinish, onBack }, ref) => {
    const model = getQuery();
    const [state, setState] = useState<DefinitionsModel>({
        vegan: !!model.vegan,
        organic: !!model.organic,
        pregnant: !!model.pregnant,
        period: model.period || Periods[0],
        activeList: !!model.activeList,
    });
    const [openActive, setOpenActive] = useState(false);
    const [openInfo, setOpenInfo] = useState(false);
    const theme = useTheme();

    const handleChange = (name: keyof DefinitionsModel, checked: boolean) => {
        setState((prev) => ({ ...prev, [name]: checked }));
    };

    const handleContinue = () => {
        setQuery(state);
        onFinish();
    };

    useImperativeHandle(ref, () => ({ submit: handleContinue }));

    const periodOptions = Periods.map((p) => ({ value: p, label: getPeriodLabel(p) }));

    return (
        <Box>
            {/* Período — segment */}
            <Typography sx={{ fontSize: 13.5, fontWeight: 500, color: "text.secondary", mb: "8px" }}>Período da formulação</Typography>
            <ChoiceChips<string>
                options={periodOptions}
                value={state.period}
                onChange={(v) => {
                    if (!model.canEdit) return;
                    setState((prev) => ({ ...prev, period: v }));
                }}
                sx={{ mb: "24px", flexWrap: "wrap", opacity: model.canEdit ? 1 : 0.5, pointerEvents: model.canEdit ? "auto" : "none" }}
            />

            {/* Switches — info button explains the filter toggles */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", mb: "4px" }}>
                <Tooltip title="Informação sobre os botões">
                    <IconButton size="small" onClick={() => setOpenInfo(true)} sx={{ color: "text.secondary", p: "2px" }}>
                        <InfoIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            </Box>
            <ToggleRow
                label="A paciente é gestante?"
                helper="Filtra ativos adequados para gestantes."
                checked={state.pregnant}
                disabled={!model.canEdit}
                onChange={(v) => handleChange("pregnant", v)}
            />
            <ToggleRow
                label="Somente produtos veganos / cruelty free?"
                helper="Inclui apenas ativos com certificação vegana."
                checked={state.vegan}
                disabled={!model.canEdit}
                onChange={(v) => handleChange("vegan", v)}
            />
            <ToggleRow
                label="Somente produtos orgânicos?"
                helper="Filtra ingredientes de origem orgânica."
                checked={state.organic}
                disabled={!model.canEdit}
                onChange={(v) => handleChange("organic", v)}
            />

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: "14px", gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>Já sei quais ativos quero usar!</Typography>
                        <Tooltip title="Ver detalhe" placement="top">
                            <IconButton size="small" onClick={() => setOpenActive(true)} sx={{ color: "primary.main", p: "2px" }}>
                                <InfoIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Typography sx={{ fontSize: 12, color: "text.secondary", mt: "2px" }}>
                        Troca a busca por classes pela busca direta por ativos.
                    </Typography>
                </Box>
                <Switch
                    checked={state.activeList}
                    onChange={(e) => handleChange("activeList", e.target.checked)}
                    sx={{
                        "& .MuiSwitch-track": {
                            backgroundColor: state.activeList ? undefined : theme.palette.brand.toggleOff,
                            opacity: state.activeList ? undefined : 1,
                        },
                    }}
                />
            </Box>

            {!model.canEdit && (
                <Alert severity="warning" variant="outlined" sx={{ mt: "16px" }}>
                    As definições da consulta não podem ser alteradas na edição do histórico!
                </Alert>
            )}

            <DefinitionsDialogs
                openActive={openActive}
                onCloseActive={() => setOpenActive(false)}
                openInfo={openInfo}
                onCloseInfo={() => setOpenInfo(false)}
            />
        </Box>
    );
});

DefinitionsTopical.displayName = "DefinitionsTopical";
